import { useEffect, useId, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useLang } from '../i18n'
import { api, isApiError } from '../lib/api'
import { useBidProfile } from '../lib/bid-profile'
import { navigate, versionRetentionPath } from '../lib/router'
import {
  ageText,
  bidCountsError,
  keepCount,
  keepKind,
  keepText,
  parseWhole,
  RETENTION_MAX_COUNT,
  type BidRetentionCounts,
  type RetentionPolicy,
} from '../lib/retention'

/** The draft count fields, one per tier: the merged policy's count as text, '' for a countless tier. */
function draftOf(policy: RetentionPolicy): string[] {
  return policy.tiers.map((tier) => {
    const n = keepCount(tier.keep)
    return n === null ? '' : String(n)
  })
}

/**
 * The bid's version-retention settings (AB#245) over `/v1/bids/:bid/retention` (AB#244) — how this
 * bid's restore points thin with age.
 *
 * The tiers read as a table, age → what is kept. A bid admin (profile, or tenant admin) edits the
 * COUNTS only; the ages are the tenant admin's (VersionRetentionPage) and show read-only, "set by
 * your administrator". "Reset to defaults" drops the bid's override, so the bid follows the tenant
 * again — including later changes there. The preview is the server's dry run of the SAVED policy:
 * "Applying this now would remove N versions in M parts" — refetched after every save.
 *
 * Anyone else on the bid sees a read-only card saying who manages it: the backend answers 403 to
 * every retention endpoint below the bid admin profile, so the card does not ask.
 */
export function VersionRetentionCard({ bid }: { bid: string }) {
  const { t } = useLang()
  const { isAdmin, isTenantAdmin, isResolved } = useBidProfile(bid)

  return (
    <section className="retention-card" aria-label={t('retention_title')}>
      <div className="retention-head">
        <span className="home-eyebrow">{t('retention_title')}</span>
      </div>
      <p className="retention-hint">{t('retention_intro')}</p>
      <p className="retention-hint">{t('retention_tagged_note')}</p>
      {!isResolved ? (
        <p aria-live="polite">…</p>
      ) : isAdmin ? (
        <RetentionCountsForm bid={bid} isTenantAdmin={isTenantAdmin} />
      ) : (
        <p className="retention-readonly">{t('retention_readonly')}</p>
      )}
    </section>
  )
}

function RetentionCountsForm({ bid, isTenantAdmin }: { bid: string; isTenantAdmin: boolean }) {
  const { t } = useLang()
  const qc = useQueryClient()
  const ids = useId()
  const [draft, setDraft] = useState<string[]>([])
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [status, setStatus] = useState('')

  const settings = useQuery({ queryKey: ['retention', bid], queryFn: () => api.bidRetention(bid) })
  const preview = useQuery({ queryKey: ['retention-preview', bid], queryFn: () => api.bidRetentionPreview(bid) })
  const data = settings.data

  // The fields follow the server: on load and after every save or reset.
  useEffect(() => {
    if (data) setDraft(draftOf(data.policy))
  }, [data])

  if (settings.isError) {
    return (
      <p className="retention-error" role="alert">
        {t('retention_load_failed')}
      </p>
    )
  }
  if (!data) return <p aria-live="polite">…</p>

  const tiers = data.policy.tiers
  const saved = draftOf(data.policy)
  const dirty = draft.some((v, i) => v !== saved[i])

  async function put(counts: BidRetentionCounts): Promise<void> {
    setSaving(true)
    setError('')
    setStatus('')
    try {
      const view = await api.setBidRetention(bid, counts)
      qc.setQueryData(['retention', bid], view)
      await qc.invalidateQueries({ queryKey: ['retention-preview', bid] })
      setStatus(t('retention_saved'))
    } catch (e) {
      setError(isApiError(e) && e.serverMessage ? e.serverMessage : t('retention_save_failed'))
    } finally {
      setSaving(false)
    }
  }

  function save(): void {
    if (!data) return
    // A count equal to the tenant's is no override (null): the bid then follows the tenant's later changes.
    const counts: BidRetentionCounts = data.tenant.tiers.map((tier, i) => {
      if (keepCount(tier.keep) === null) return null
      const n = parseWhole(draft[i] ?? '')
      return n === keepCount(tier.keep) ? null : n
    })
    const why = bidCountsError(counts, data.tenant)
    if (why) {
      setError(t(why))
      return
    }
    void put(counts)
  }

  const first = tiers[0]

  return (
    <>
      <p className="visually-hidden" role="status" aria-live="polite">
        {status}
      </p>
      <div className="retention-scroll">
        <table className="kbtable retention-table">
          <caption className="visually-hidden">{t('retention_caption')}</caption>
          <thead>
            <tr>
              <th scope="col">{t('retention_col_age')}</th>
              <th scope="col">{t('retention_col_keep')}</th>
            </tr>
          </thead>
          <tbody>
            {first && (
              <tr>
                <th scope="row">{t('retention_age_under').replace('{age}', ageText(t, first.after))}</th>
                <td>{t('retention_keep_all')}</td>
              </tr>
            )}
            {tiers.map((tier, i) => {
              const age = t('retention_age_after').replace('{age}', ageText(t, tier.after))
              const kind = keepKind(tier.keep)
              return (
                <tr key={i}>
                  <th scope="row">{age}</th>
                  <td>
                    {kind === 'all' || kind === 'runs' ? (
                      keepText(t, tier.keep)
                    ) : (
                      <span className="retention-count">
                        <input
                          id={`${ids}-count-${i}`}
                          type="number"
                          min={1}
                          max={RETENTION_MAX_COUNT}
                          step={1}
                          inputMode="numeric"
                          aria-label={t('retention_count_label')
                            .replace('{keep}', t(`retention_keep_${kind}`))
                            .replace('{age}', age.toLowerCase())}
                          value={draft[i] ?? saved[i] ?? ''}
                          disabled={saving}
                          onChange={(e) => {
                            const value = e.target.value
                            setDraft((prev) => prev.map((v, j) => (j === i ? value : v)))
                          }}
                        />
                        <span aria-hidden="true">{t(`retention_keep_${kind}`)}</span>
                      </span>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      <p className="retention-hint">
        {t('retention_times_by_admin')}{' '}
        {isTenantAdmin && (
          <button type="button" className="retention-link" onClick={() => navigate(versionRetentionPath)}>
            {t('retention_edit_defaults')}
          </button>
        )}
      </p>

      <div className="retention-actions">
        <button type="button" className="mini ok" disabled={saving || !dirty} onClick={save}>
          {t('retention_save')}
        </button>
        <button
          type="button"
          className="mini no"
          disabled={saving || !(data.counts ?? []).some((n) => n !== null)}
          onClick={() => void put([])}
        >
          {t('retention_reset')}
        </button>
      </div>

      {error && (
        <p className="retention-error" role="alert">
          {error}
        </p>
      )}

      {preview.data && (
        <p className="retention-preview" aria-live="polite">
          {preview.data.wouldRemove === 0
            ? t('retention_preview_none')
            : t('retention_preview')
                .replace('{n}', String(preview.data.wouldRemove))
                .replace('{m}', String(preview.data.rooms))}
        </p>
      )}
      {dirty && <p className="retention-hint">{t('retention_preview_pending')}</p>}
    </>
  )
}
